"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useCallback, useEffect } from "react";
import { useFansUnitedSDK } from "fansunited-data-layer/client";
import type { Category, ClassicQuizParticipation, ClassicQuizResponse, PollVote, PollVotesResponse, PotmVote, PotmVotesResponse } from "./types";
const PAGE_SIZE = 12;
interface PaginatedState<T> {
  items: T[];
  cursor: string | null;
  loading: boolean;
  loadingMore: boolean;
}
const initialState = <T,>(): PaginatedState<T> => ({
  items: [],
  cursor: null,
  loading: true,
  loadingMore: false
});
export function useGamesVotesData(activeCategory) {
  const $ = _c(31);
  const sdk = useFansUnitedSDK();
  const [quizzes, setQuizzes] = useState(initialState);
  const [polls, setPolls] = useState(initialState);
  const [potm, setPotm] = useState(initialState);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Set();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [fetched, setFetched] = useState(t0);
  let t1;
  if ($[1] !== sdk) {
    t1 = async startAfter => {
      if (!sdk) {
        return;
      }
      const params = {
        limit: PAGE_SIZE
      };
      if (startAfter) {
        params.startAfter = startAfter;
      }
      try {
        const response = (await sdk.miniGames.getMyClassicQuizParticipationsWithPagination(params)) as unknown as ClassicQuizResponse;
        setQuizzes(prev_1 => ({
          ...prev_1,
          items: startAfter ? [...prev_1.items, ...response.data] : response.data,
          cursor: response.meta?.pagination?.nextPageStartsAfter || null
        }));
      } catch {
        if (!startAfter) {
          setQuizzes(_temp);
        }
        setQuizzes(_temp2);
      }
    };
    $[1] = sdk;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const fetchQuizzes = t1;
  let t2;
  if ($[3] !== sdk) {
    t2 = async startAfter_0 => {
      if (!sdk) {
        return;
      }
      const params_0 = {
        limit: PAGE_SIZE
      };
      if (startAfter_0) {
        params_0.startAfter = startAfter_0;
      }
      try {
        const response_0 = (await sdk.voting.getMyPollsVotes(params_0)) as unknown as PollVotesResponse;
        const votes = response_0.data;
        const enriched = await Promise.all(votes.map(async vote => {
          if (vote.pollModel?.title) {
            return vote;
          }
          try {
            const full = (await sdk.voting.getMyPollVote(vote.pollId, true)) as unknown as PollVote;
            return {
              ...vote,
              pollModel: full.pollModel,
              optionModel: full.optionModel,
              optionsModels: full.optionsModels
            };
          } catch {
            return vote;
          }
        }));
        setPolls(prev_4 => ({
          ...prev_4,
          items: startAfter_0 ? [...prev_4.items, ...enriched] : enriched,
          cursor: response_0.meta?.pagination?.nextPageStartsAfter || null
        }));
      } catch {
        if (!startAfter_0) {
          setPolls(_temp3);
        }
        setPolls(_temp4);
      }
    };
    $[3] = sdk;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const fetchPolls = t2;
  let t3;
  if ($[5] !== sdk) {
    t3 = async startAfter_1 => {
      if (!sdk) {
        return;
      }
      const params_1 = {
        limit: PAGE_SIZE
      };
      if (startAfter_1) {
        params_1.startAfter = startAfter_1;
      }
      try {
        const response_1 = (await sdk.voting.getOwnVotesForPlayerOfTheMatch(params_1)) as unknown as PotmVotesResponse;
        setPotm(prev_7 => ({
          ...prev_7,
          items: startAfter_1 ? [...prev_7.items, ...response_1.data] : response_1.data,
          cursor: response_1.meta?.pagination?.nextPageStartsAfter || null
        }));
      } catch {
        if (!startAfter_1) {
          setPotm(_temp5);
        }
        setPotm(_temp6);
      }
    };
    $[5] = sdk;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const fetchPotm = t3;
  let t4;
  let t5;
  if ($[7] !== activeCategory || $[8] !== fetchPolls || $[9] !== fetchPotm || $[10] !== fetchQuizzes || $[11] !== fetched || $[12] !== sdk) {
    t4 = () => {
      if (!sdk || fetched.has(activeCategory)) {
        return;
      }
      const fetchCategory = async () => {
        if (activeCategory === "quizzes") {
          setQuizzes(_temp7);
          await fetchQuizzes();
          setQuizzes(_temp8);
        } else {
          if (activeCategory === "polls") {
            setPolls(_temp9);
            await fetchPolls();
            setPolls(_temp10);
          } else {
            if (activeCategory === "potm") {
              setPotm(_temp11);
              await fetchPotm();
              setPotm(_temp12);
            }
          }
        }
        setFetched(prev_14 => new Set(prev_14).add(activeCategory));
      };
      fetchCategory();
    };
    t5 = [sdk, activeCategory, fetched, fetchQuizzes, fetchPolls, fetchPotm];
    $[7] = activeCategory;
    $[8] = fetchPolls;
    $[9] = fetchPotm;
    $[10] = fetchQuizzes;
    $[11] = fetched;
    $[12] = sdk;
    $[13] = t4;
    $[14] = t5;
  } else {
    t4 = $[13];
    t5 = $[14];
  }
  useEffect(t4, t5);
  let t6;
  if ($[15] !== fetchQuizzes || $[16] !== quizzes) {
    t6 = async () => {
      if (!quizzes.cursor) {
        return;
      }
      setQuizzes(_temp13);
      await fetchQuizzes(quizzes.cursor);
      setQuizzes(_temp14);
    };
    $[15] = fetchQuizzes;
    $[16] = quizzes;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const loadMoreQuizzes = t6;
  let t7;
  if ($[18] !== fetchPolls || $[19] !== polls) {
    t7 = async () => {
      if (!polls.cursor) {
        return;
      }
      setPolls(_temp15);
      await fetchPolls(polls.cursor);
      setPolls(_temp16);
    };
    $[18] = fetchPolls;
    $[19] = polls;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const loadMorePolls = t7;
  let t8;
  if ($[21] !== fetchPotm || $[22] !== potm) {
    t8 = async () => {
      if (!potm.cursor) {
        return;
      }
      setPotm(_temp17);
      await fetchPotm(potm.cursor);
      setPotm(_temp18);
    };
    $[21] = fetchPotm;
    $[22] = potm;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const loadMorePotm = t8;
  let t9;
  if ($[24] !== loadMorePolls || $[25] !== loadMorePotm || $[26] !== loadMoreQuizzes || $[27] !== polls || $[28] !== potm || $[29] !== quizzes) {
    t9 = {
      quizzes,
      polls,
      potm,
      loadMoreQuizzes,
      loadMorePolls,
      loadMorePotm
    };
    $[24] = loadMorePolls;
    $[25] = loadMorePotm;
    $[26] = loadMoreQuizzes;
    $[27] = polls;
    $[28] = potm;
    $[29] = quizzes;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
}
function _temp18(prev_20) {
  return {
    ...prev_20,
    loadingMore: false
  };
}
function _temp17(prev_19) {
  return {
    ...prev_19,
    loadingMore: true
  };
}
function _temp16(prev_18) {
  return {
    ...prev_18,
    loadingMore: false
  };
}
function _temp15(prev_17) {
  return {
    ...prev_17,
    loadingMore: true
  };
}
function _temp14(prev_16) {
  return {
    ...prev_16,
    loadingMore: false
  };
}
function _temp13(prev_15) {
  return {
    ...prev_15,
    loadingMore: true
  };
}
function _temp12(prev_13) {
  return {
    ...prev_13,
    loading: false
  };
}
function _temp11(prev_12) {
  return {
    ...prev_12,
    loading: true
  };
}
function _temp10(prev_11) {
  return {
    ...prev_11,
    loading: false
  };
}
function _temp9(prev_10) {
  return {
    ...prev_10,
    loading: true
  };
}
function _temp8(prev_9) {
  return {
    ...prev_9,
    loading: false
  };
}
function _temp7(prev_8) {
  return {
    ...prev_8,
    loading: true
  };
}
function _temp6(prev_6) {
  return {
    ...prev_6,
    cursor: null
  };
}
function _temp5(prev_5) {
  return {
    ...prev_5,
    items: []
  };
}
function _temp4(prev_3) {
  return {
    ...prev_3,
    cursor: null
  };
}
function _temp3(prev_2) {
  return {
    ...prev_2,
    items: []
  };
}
function _temp2(prev_0) {
  return {
    ...prev_0,
    cursor: null
  };
}
function _temp(prev) {
  return {
    ...prev,
    items: []
  };
}